import { Component, Input, OnInit, inject, Output, EventEmitter } from '@angular/core';
import {
  FormBuilder,
  FormArray,
  FormGroup,
  Validators,
  FormControl,
  ReactiveFormsModule
} from '@angular/forms';

import { RmsModule } from '@rms';
import { InventoryDataService, WebService, lastValueFrom, PromptService, PopupService, RmsMessageService } from '@services';

export interface SkuRow {
  Active: boolean;
  Country: number | null;
  DNAJson: string | null;
  Decaf: boolean;

  DryingProcess: number | null;
  ExportEndMonth: number | null;
  ExportStartMonth: number | null;

  FairTrade: boolean;
  FarmStory: string | null;
  FreightCostFOBToNY: number | null;
  GrowingRegions: string | null;

  HarvestEndMonth: number | null;
  HarvestStartMonth: number | null;

  InventoryTypeID: number | null;          // ✅ PK
  InventoryTypeName: string | null;

  MillingProcess: number | null;
  Notes: string | null;

  Organic: boolean;
  Rainforest: boolean;

  Region: number | null;
  Robusta: boolean;
  SMBC: boolean;
  ShadeGrown: boolean;

  SubRegion: number | null;
  VarietalConventional: boolean;

  GroupName: string | null; 
}

type DnaModel = {
  genes: Array<{ key: number; traits: number[] }>;
};

type basicForm = FormGroup<{
  Active: FormControl<boolean>;
  InventoryTypeID: FormControl<number | null>;
  InventoryTypeName: FormControl<string | null>;
  Decaf: FormControl<boolean>;
  GroupName: FormControl<string | null>;
}>
type SkuForm = FormGroup<{

  // location
  Region: FormControl<number | null>;
  Country: FormControl<number | null>;
  SubRegion: FormControl<number | null>;

  // processes
  DryingProcess: FormControl<number | null>;
  MillingProcess: FormControl<number | null>;

  // months
  ExportStartMonth: FormControl<number | null>;
  ExportEndMonth: FormControl<number | null>;
  HarvestStartMonth: FormControl<number | null>;
  HarvestEndMonth: FormControl<number | null>;

  // misc
  FreightCostFOBToNY: FormControl<number | null>;
  GrowingRegions: FormControl<string | null>;
  FarmStory: FormControl<string | null>;
  Notes: FormControl<string | null>;

  // cert flags
  FairTrade: FormControl<boolean>;
  Organic: FormControl<boolean>;
  Rainforest: FormControl<boolean>;
  ShadeGrown: FormControl<boolean>;
  SMBC: FormControl<boolean>;

  // coffee flags
  Robusta: FormControl<boolean>;
  VarietalConventional: FormControl<boolean>;

  // DNA structured
  dna: FormGroup
}>;
interface Gene {
  GeneKey: number;
  GeneName: string;
  MultiSelect: boolean;
  SeqNum: number;
}

interface Trait {
  TraitKey: number;
  GeneKey: number;
  TraitName: string;
  DefaultForGene: boolean;
}
@Component({
  selector: 'app-coffee-data',
  standalone: true,
  imports: [RmsModule, ReactiveFormsModule],
  templateUrl: './coffee-data.component.html',
  styleUrl: './coffee-data.component.scss'
})
export class CoffeeDataComponent implements OnInit {
  @Input() Product: any = null;
  @Output() SkuCreated = new EventEmitter<number>();
  @Output() SkuCancelled = new EventEmitter<void>();
  @Output() NewSkuExists = new EventEmitter<number>();
  @Output() RequestBack = new EventEmitter<{ hasChanges: boolean }>();
  private fb = inject(FormBuilder);
  Genes: Gene[] = [];
  Traits: Trait[] = [];
  TraitsByGene = new Map<number, Trait[]>();
  ImagePath: string = '';
  // store lookup data you already load (regions/countries/subregions)
  InventoryData: any = {
    Regions: [],
    Countries: [],
    SubRegions: []
  };
  GroupSearch = '';

  AssignedGroups: Array<{ id: number; name: string; path?: string }> = [
    // starter wireframe data (replace with API later)
    { id: 101, name: 'Bolivia Organic FT', path: 'Bolivia Organic > Bolivia Organic FT' }
  ];
  CoffeeReports: any[] = [
    { label: 'SKU Marketing Card' },
    { label: 'Cuppings By SKU' },
  ]
  trackGroup = (_: number, g: { id: number }) => g.id;

  ProductData: SkuRow | null = null;
  InventoryImages: any[] = [];
  form: SkuForm = this.buildSkuForm();
  basicForm: basicForm = this.buildBasicForm();
  dataSource: any = [
    { name: 'Bolivia Organic FT', children: [{name: '7668 - Organic Bolivia FAIR TRADE-APROCAFE (E)'}]}
  ]
  hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;
  childrenAccessor = (node: any) => node.children ?? [];
  AutoFillProductID: any | null = null;
  currentPage = 0;
  pageSize = 5;

  get pagedImages() {
    const start = this.currentPage * this.pageSize;
    return this.InventoryImages.slice(start, start + this.pageSize);
  }

  get totalPages() {
    return Math.ceil(this.InventoryImages.length / this.pageSize);
  }

  constructor(
    private inventoryDataService: InventoryDataService,
    private web: WebService,
    private prompt: PromptService,
    private popup: PopupService,
    private message: RmsMessageService
  ) { }

  async ngOnInit() {
    console.log(this.Product)
    await this.loadProductData();


    this.form.controls.Region.valueChanges.subscribe(region => {
      const countryCtrl = this.form.controls.Country;
      const subRegionCtrl = this.form.controls.SubRegion;

      if (region) {
        countryCtrl.enable({ emitEvent: false });
      } else {
        countryCtrl.disable({ emitEvent: false });
        subRegionCtrl.disable({ emitEvent: false });
      }

      // reset dependent values
      countryCtrl.setValue(null, { emitEvent: false });
      subRegionCtrl.setValue(null, { emitEvent: false });
    });

    this.form.controls.Country.valueChanges.subscribe(country => {
      const subRegionCtrl = this.form.controls.SubRegion;

      if (country) {
        subRegionCtrl.enable({ emitEvent: false });
      } else {
        subRegionCtrl.disable({ emitEvent: false });
      }

      subRegionCtrl.setValue(null, { emitEvent: false });
    });

    this.ImagePath = `inventory-images/${this.Product?.InventoryTypeID}`;
    await this.getImages();
  }

  trackPhoto = (_: number, p: { id: number }) => p.id;

  openPhotoUploadWireframe() {
    // Wireframe placeholder
    console.log('Open upload dialog (wireframe)');
  }


  async getImages() {
    if (!this.ProductData) return;

    const allFiles = await this.fetchImages(
      this.ImagePath
    );

    this.InventoryImages = allFiles.filter(file => {
      if (file.type !== 'file') return false; // extra safety

      const name = file.name.toLowerCase();

      return (
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png') ||
        name.endsWith('.webp') ||
        name.endsWith('.gif')
      );
    });
  }
  async UploadImage (file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', this.ImagePath)
    formData.append('fileName', this.ProductData?.InventoryTypeID + '_' + (this.InventoryImages.length + 1) + '.' + file.name.split('.').pop());

    await lastValueFrom(this.web.getWebCall('files', 'upload', { body: formData }))
    await this.getImages();
  }
  async DeleteImage(fileName: string) {
    const { closed$ } = this.prompt.open({
      promptText: `Are you sure you want to delete ${fileName}?`,
      buttons: [{ label: 'Delete', value: true }],
    });

    closed$.subscribe(async (result) => {
      if (result) {
        await lastValueFrom(this.web.getWebCall('files', 'delete', {
          params: [{name: 'path', value: this.ImagePath + '/' + fileName}]
        }));

        this.getImages();
      }
    })


  }

  async onFilesDropped(files: File[]) {
    const images = files.filter(f => f.type.startsWith('image/'));
    console.log(images);
    await Promise.all(images.map(file => this.UploadImage(file)));
  }


  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);

    const images = files.filter(f => f.type.startsWith('image/'));

    if (!images.length) {
      console.error('No valid images selected');
      return;
    }

    console.log('Selected images:', images);

    await Promise.all(
      images.map(async (file) => {
        try {
          await this.UploadImage(file);
        } catch (err) {
          console.error('Upload failed:', file.name, err);
        }
      })
    );

    // ✅ optional: reset input so same file can be selected again
    input.value = '';
  }


  async loadProductData() {
    // load dropdown lists
    const data = await this.inventoryDataService.getData({ keys: ['Regions', 'Countries', 'SubRegions', 'Genes', 'Traits', 'Products'] });
    this.InventoryData = { ...this.InventoryData, ...data };
    this.Genes = data['Genes'];
    this.Traits = data['Traits'];
    console.log(this.InventoryData);
    this.form.setControl(
      'dna',
      this.buildDnaForm(this.Genes, this.Traits)
    );
    console.log('Product:', this.Product);
    // load sku data row
    const resp = await lastValueFrom(
      this.web.getWebCall('inventory', 'product_data', {
        params: [{ name: 'InventoryTypeID', value: this.Product?.InventoryTypeID }]
      })
    );

    console.log('ProductData Response:', resp);

    // Normalize response to a single SkuRow
    const row = this.normalizeSkuRow(resp);
    this.CreateForm(row);
  }

  get SelectedRegion() {
    return this.form.controls.Region.value;
  }
  get SelectedCountry() {
    return this.form.controls.Country.value;
  }
  get FilteredCountries() {
    return this.InventoryData.Countries.filter((c: any) => c.RegionID === this.SelectedRegion);
  }
  get FilteredSubRegions() {
    return this.InventoryData.SubRegions.filter((s: any) => s.CountryID === this.SelectedCountry);
  }
  traitsByGene(gene: any) {
    if (!this.TraitsByGene.has(gene.GeneKey)) {
      return [];
    }
    return this.TraitsByGene.get(gene.GeneKey)!.map(t => ({
      value: t.TraitKey,
      label: t.TraitName
    }));
  }

  addGroupToSku(node: any) {
    // wireframe stand-in for drag/drop: click "playlist_add"
    const id = node?.id ?? this.hashNameToId(node?.name ?? 'group');
    const name = node?.name ?? 'Unnamed Group';

    if (this.AssignedGroups.some(g => g.id === id)) return;

    this.AssignedGroups = [
      ...this.AssignedGroups,
      { id, name, path: node?.path } // path optional
    ];
  }

  removeGroupFromSku(g: { id: number }) {
    this.AssignedGroups = this.AssignedGroups.filter(x => x.id !== g.id);
  }

  // helper to create stable-ish IDs for wireframe nodes that don't have IDs yet
  private hashNameToId(name: string): number {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = ((h << 5) - h) + name.charCodeAt(i);
    return Math.abs(h);
  }

  /** If API returns {data:[...]} or [...] or direct object, normalize it */
  private normalizeSkuRow(resp: any): SkuRow {
    const candidate =
      resp?.data?.[0] ??
      resp?.[0] ??
      resp;

    // If your API uses different casing, map here.
    return candidate as SkuRow;
  }

  /** You were calling CreateForm(ProductData) — here it is */
  CreateForm(row: SkuRow) {
    this.ProductData = row;
    this.loadSkuRow(row);
  }
  buildBasicForm(): any {
    return this.fb.group({
      InventoryTypeName: this.fb.nonNullable.control(''),
      InventoryTypeID: this.fb.nonNullable.control(null),
      Active: this.fb.nonNullable.control(false),
      Decaf: this.fb.nonNullable.control(false),
      GroupName: this.fb.control<string | null>(null)
    })
  }
  buildSkuForm(): SkuForm {
    return this.fb.group({
      Region: this.fb.control<number | null>(null),
      Country: this.fb.control<number | null>({
        value: null,
        disabled: true
      }),
      SubRegion: this.fb.control<number | null>({
        value: null,
        disabled: true // disable until country selected
      }),

      DryingProcess: this.fb.control<number | null>(null),
      MillingProcess: this.fb.control<number | null>(null),

      ExportStartMonth: this.fb.control<number | null>(null),
      ExportEndMonth: this.fb.control<number | null>(null),
      HarvestStartMonth: this.fb.control<number | null>(null),
      HarvestEndMonth: this.fb.control<number | null>(null),

      FreightCostFOBToNY: this.fb.control<number | null>(null),
      GrowingRegions: this.fb.control<string | null>(null),
      FarmStory: this.fb.control<string | null>(null),
      Notes: this.fb.control<string | null>(null),

      FairTrade: this.fb.nonNullable.control(false),
      Organic: this.fb.nonNullable.control(false),
      Rainforest: this.fb.nonNullable.control(false),
      ShadeGrown: this.fb.nonNullable.control(false),
      SMBC: this.fb.nonNullable.control(false),

      Robusta: this.fb.nonNullable.control(false),
      VarietalConventional: this.fb.nonNullable.control(true),

      // ✅ THIS is the DNA editor
      dna: this.fb.group({})
    });
  }

  // ---------- Load row -> patch form ----------
  loadSkuRow(row: SkuRow) {
    // Patch scalar values
    this.form.patchValue({

      Region: row.Region,
      Country: row.Country,
      SubRegion: row.SubRegion,

      DryingProcess: row.DryingProcess,
      MillingProcess: row.MillingProcess,

      ExportStartMonth: row.ExportStartMonth,
      ExportEndMonth: row.ExportEndMonth,
      HarvestStartMonth: row.HarvestStartMonth,
      HarvestEndMonth: row.HarvestEndMonth,

      FreightCostFOBToNY: row.FreightCostFOBToNY,

      GrowingRegions: row.GrowingRegions,
      FarmStory: row.FarmStory,
      Notes: row.Notes,

      FairTrade: row.FairTrade,
      Organic: row.Organic,
      Rainforest: row.Rainforest,
      ShadeGrown: row.ShadeGrown,
      SMBC: row.SMBC,

      Robusta: row.Robusta,
      VarietalConventional: row.VarietalConventional
    });
    this.basicForm.patchValue({
      Active: row.Active,
      InventoryTypeID: row.InventoryTypeID,
      InventoryTypeName: row.InventoryTypeName,
      GroupName: row.GroupName ?? 'N/A'
    })
    // ✅ PK should not be edited once loaded
    if (row.InventoryTypeID != null) {
      this.basicForm.controls.InventoryTypeID.disable({ emitEvent: false });
    }

    // Rebuild DNA array
    this.applyDnaJsonToForm(row.DNAJson);


    if (row.Region) {
      this.form.controls.Country.enable({ emitEvent: false });
    }

    if (row.Country) {
      this.form.controls.SubRegion.enable({ emitEvent: false });
    }


    // Mark as pristine after load (so your submit button logic works)
    this.form.markAsPristine();
    this.basicForm.markAsPristine();
  }

  // ---------- DNA parse/serialize ----------
  private applyDnaJsonToForm(dnaJson: string | null) {
    const dnaGroup = this.form.controls.dna as FormGroup;

    // 1) Reset first so defaults don't "win"
    for (const gene of this.Genes) {
      const ctrl = dnaGroup.get(String(gene.GeneKey)) as FormControl | null;
      if (!ctrl) continue;

      ctrl.setValue(gene.MultiSelect ? [] : null, { emitEvent: false });
    }

    // 2) Apply saved values
    if (!dnaJson) {
      dnaGroup.updateValueAndValidity({ emitEvent: false });
      return;
    }

    let parsed: DnaModel | null = null;
    try {
      parsed = JSON.parse(dnaJson) as DnaModel;
    } catch {
      // invalid json; keep reset state
      dnaGroup.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const byGeneKey = new Map<number, number[]>(
      (parsed.genes ?? []).map(g => [g.key, g.traits ?? []])
    );

    for (const gene of this.Genes) {
      const traits = byGeneKey.get(gene.GeneKey);
      if (!traits) continue;

      // Optional safety: only allow TraitKeys that belong to this GeneKey
      const allowed = new Set(
        (this.TraitsByGene.get(gene.GeneKey) ?? []).map(t => t.TraitKey)
      );
      const filtered = traits.filter(tk => allowed.has(tk));

      const ctrl = dnaGroup.get(String(gene.GeneKey)) as FormControl | null;
      if (!ctrl) continue;

      ctrl.setValue(
        gene.MultiSelect ? filtered : (filtered[0] ?? null),
        { emitEvent: false }
      );
    }

    dnaGroup.updateValueAndValidity({ emitEvent: false });
  }
  getDnaJsonString(): string {
    const dnaGroup = this.form.controls.dna as FormGroup;

    const genes = Object.entries(dnaGroup.getRawValue())
      .map(([geneKey, value]) => {
        const traits = Array.isArray(value)
          ? value
          : value != null
            ? [value]
            : [];

        return traits.length
          ? { key: Number(geneKey), traits }
          : null;
      })
      .filter(Boolean);

    return JSON.stringify({ genes });
  }
  toSkuRowPayload(original?: Partial<SkuRow>): SkuRow {
    const v = this.form.getRawValue(); // includes disabled InventoryTypeID
    const b = this.basicForm.getRawValue();
    return {
      ...(original ?? ({} as any)),

      Active: b.Active,
      InventoryTypeID: b.InventoryTypeID,
      InventoryTypeName: b.InventoryTypeName,
      Decaf: b.Decaf,

      Region: v.Region,
      Country: v.Country,
      SubRegion: v.SubRegion,

      DryingProcess: v.DryingProcess,
      MillingProcess: v.MillingProcess,

      ExportStartMonth: v.ExportStartMonth,
      ExportEndMonth: v.ExportEndMonth,
      HarvestStartMonth: v.HarvestStartMonth,
      HarvestEndMonth: v.HarvestEndMonth,

      FreightCostFOBToNY: v.FreightCostFOBToNY,

      GrowingRegions: v.GrowingRegions,
      FarmStory: v.FarmStory,
      Notes: v.Notes,

      FairTrade: v.FairTrade,
      Organic: v.Organic,
      Rainforest: v.Rainforest,
      ShadeGrown: v.ShadeGrown,
      SMBC: v.SMBC,

      Robusta: v.Robusta,
      VarietalConventional: v.VarietalConventional,

      DNAJson: this.getDnaJsonString()
    };
  }
  buildDnaForm(genes: Gene[], traits: Trait[]): FormGroup {
    const group: Record<string, FormControl<any>> = {};

    this.TraitsByGene.clear();
    for (const t of traits) {
      if (!this.TraitsByGene.has(t.GeneKey)) this.TraitsByGene.set(t.GeneKey, []);
      this.TraitsByGene.get(t.GeneKey)!.push(t);
    }

    for (const gene of [...genes].sort((a, b) => a.SeqNum - b.SeqNum)) {
      const defaults =
        this.TraitsByGene.get(gene.GeneKey)
          ?.filter(t => t.DefaultForGene)
          .map(t => t.TraitKey) ?? [];

      group[String(gene.GeneKey)] = new FormControl(
        gene.MultiSelect ? defaults : (defaults[0] ?? null)
      );
    }

    return this.fb.group(group);
  }
  get SortedGenes(): Gene[] {
    // Single-select first, then multi-select
    return [...this.Genes].sort((a, b) => {
      // false (0) before true (1)
      const multiA = a.MultiSelect ? 1 : 0;
      const multiB = b.MultiSelect ? 1 : 0;

      if (multiA !== multiB) return multiA - multiB;

      // within each group, keep SeqNum ordering
      return (a.SeqNum ?? 0) - (b.SeqNum ?? 0);
    });
  }
  async SkuExists(): Promise<boolean> {
    const skuName = this.basicForm.controls.InventoryTypeName.value?.trim();

    if (!skuName) {
      this.basicForm.controls.InventoryTypeName.markAsTouched();
      this.message.error('Please enter a SKU name before saving.');
      return true; // stop submit
    }

    try {
      const resp = await lastValueFrom(
        this.web.getWebCall('inventory', '/coffee/products/exists', {
          params: [
            { name: 'InventoryTypeName', value: skuName }
          ]
        })
      );

      if (!resp[0]?.InventoryTypeID) {
        return false; // does not exist, continue creating
      }

      this.message.error(
        `SKU "${resp[0].InventoryTypeName ?? skuName}" already exists. Loading the existing SKU instead.`
      );

      this.NewSkuExists.emit(resp[0].InventoryTypeID);
      return true; // exists, stop submit
    } catch (err) {
      console.error('SKU exists check failed:', err);
      this.message.error('Unable to check if this SKU already exists.');
      return true; // safer to stop than create a duplicate
    }
  }
  async submit() {
    const payload = this.toSkuRowPayload(this.ProductData ?? undefined);
    console.log('Submitting SKU payload:', payload);


    if (!this.Product?.InventoryTypeID) {
      const exists = await this.SkuExists();

      if (exists) {
        return;
      }
    }


    const res = await lastValueFrom(this.web.getWebCall('inventory', '/coffee/products/update', { body: payload }))
    const id = res[0]?.InventoryTypeID;

    const resp = await lastValueFrom(
      this.web.getWebCall('inventory', 'product_data', {
        params: [{ name: 'InventoryTypeID', value: id }]
      })
    );
    this.SkuCreated.emit(id);
    if (this.Product.InventoryTypeID) {
      //const row = this.normalizeSkuRow(resp);
      //this.CreateForm(row);

      //this.form.markAsPristine();
      this.message.success('SKU updated successfully!');
    }
    else {

      this.message.success('SKU created successfully!');
    }
    console.log('ProductData Response:', resp);

    // Normalize response to a single SkuRow

  }
  CancelSku() {
    this.SkuCancelled.emit();
  }
  private async fetchImages(path?: string): Promise<any[]> {
    const endpoint = path ? `/list?path=${encodeURIComponent(path)}` : '/list';
    return await lastValueFrom(this.web.getWebCall('files', endpoint));
  }
  async ViewImage(path: string) {
    this.popup.openFileViewer({
      title: 'Image Preview',
      fileUrl: path,
    })
  }

  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
    }
  }
  async UpdateAutofill(InventoryTypeID: number) {
    const resp = await lastValueFrom(
      this.web.getWebCall('inventory', 'product_data', {
        params: [{ name: 'InventoryTypeID', value: InventoryTypeID }]
      })
    );
    const newProduct = { ...resp[0], InventoryTypeID: null, InventoryTypeName: this.basicForm.controls.InventoryTypeName.value };
    const row = this.normalizeSkuRow(newProduct);
    this.CreateForm(row);
  }
  hasUnsavedChanges(): boolean {
    return this.form.dirty || this.basicForm.dirty;
  }
}
